import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { FaEllipsisV, FaExclamationTriangle, FaFileExcel, FaFilter, FaHeadset, FaPaperclip, FaPaperPlane, FaPencilAlt, FaPlane, FaSearch, FaTicketAlt, FaTimes, FaUpload } from 'react-icons/fa';
import ErpTemplate from '../../components/ErpTemplate';
import ReactSelect from '../../components/ReactSelect';
import { FinCheck, FinChecks, FinField, FinInput } from '../../components/FinField';
import { useSetFullBreadcrumbTrail } from '../../context/BreadcrumbContext';
import { PopoverObservacao } from '../../components/PillObservacao';
import { baixarBlob } from '../../components/PlanilhaEmissaoAereo';
import FechamentoAereoModal, { quandoQuem } from '../../components/FechamentoAereoModal';
import { useAtendimentoAereo } from '../../components/AtendimentoAereo';
import TabelaLoading from '../../components/TabelaLoading';
import TabelaVazia from '../../components/TabelaVazia';
import api from '../../services/api';
import notify from '../../services/notify';
import '../../css/financeiro-parcela-pagar.css';

const API = process.env.REACT_APP_API_URL;

// As situações do evento (`evento.situacao`), com o nome e a cor do painel de eventos.
const SITUACOES = [
    { value: 'ativo', label: 'Aberto', tom: 'is-ok' },
    { value: 'fechado', label: 'Fechado', tom: 'is-info' },
    { value: 'cancelado', label: 'Cancelado', tom: 'is-danger' },
    { value: 'pendente', label: 'Pendente', tom: 'is-warn' },
];
const SITUACOES_PADRAO = ['ativo', 'pendente'];
// "Todos" no select de situação: vale pelas quatro, que não ficam marcadas juntas. No estado é a lista vazia, que o
// back lê como sem filtro.
const TODAS_SITUACOES = { value: 'todos', label: 'Todos' };
// O chip "Todos" sem o x: tirá-lo deixaria a situação vazia, que é o próprio "Todos".
const ESTILO_SITUACOES = {
    multiValueLabel: (base, { data }) => (data.value === TODAS_SITUACOES.value ? { ...base, paddingRight: 6 } : base),
    multiValueRemove: (base, { data }) => (data.value === TODAS_SITUACOES.value ? { ...base, display: 'none' } : base),
};
const situacoesEscolhidas = (sel) => {
    const valores = (sel || []).map(s => s.value);
    // Escolher o "Todos" limpa as outras; escolher uma situação tira o "Todos".
    if (valores[valores.length - 1] === TODAS_SITUACOES.value) return [];
    const situacoes = valores.filter(v => v !== TODAS_SITUACOES.value);
    return situacoes.length === SITUACOES.length ? [] : situacoes;
};
// O filtro de pendências: "Qualquer pendência" e cada badge da coluna, nos grupos dela. O evento entra com qualquer
// uma das escolhidas (`EventoAereoPainelService::FILTROS_PENDENCIA`).
const QUALQUER_PENDENCIA = { value: 'qualquer', label: 'Qualquer pendência' };
const GRUPOS_PENDENCIA = [
    { titulo: 'Emissões', itens: [
        { value: 'emEmissao', label: 'Em emissão' },
        { value: 'aEmitir', label: 'A emitir' },
        { value: 'cancelados', label: 'Cancelados' },
        { value: 'emitidos', label: 'Emitidos' },
        // A sugestão vai ao RSVP no Enviar da emissão.
        { value: 'sugestaoAEnviar', label: 'Sugestão a enviar' },
        { value: 'sugestaoEnviada', label: 'Sugestão enviada' },
    ] },
    { titulo: 'Malhas', itens: [
        { value: 'semMalha', label: 'Sem malha' },
        { value: 'rascunhos', label: 'Em rascunho' },
        { value: 'malhasEmAlteracao', label: 'Alteração necessária' },
        { value: 'enviadas', label: 'Malhas enviadas' },
        { value: 'alteracoes', label: 'Alteração solicitada' },
        { value: 'canceladas', label: 'Solicitações canceladas' },
    ] },
];
// O filtro Fechado/Despachado do Gerente Aéreo e do admin (`EventoAereoFechamentoService::FILTROS`), com as opções F
// e D, como as marcas da coluna: o evento entra com qualquer uma das escolhidas.
const OPCOES_FD = [{ value: 'fechado', label: 'F' }, { value: 'despachado', label: 'D' }];
const ROTULO_PENDENCIA = Object.fromEntries([QUALQUER_PENDENCIA, ...GRUPOS_PENDENCIA.flatMap(g => g.itens)].map(o => [o.value, o.label]));
// No filtro de atendente: os eventos que ninguém atende (`EventoAereoPainelService::SEM_ATENDENTE`).
const SEM_ATENDENTE = 'sem';
const PAGE_SIZE = 20;
// Quantos nomes a célula de atendimento mostra; o resto vira "+n responsáveis", e o clique abre todos.
const NOMES_NA_CELULA = 3;
// As seções do drawer de ações da linha, nesta ordem.
const GRUPOS_DO_MENU = ['Malha e emissão', 'Atendimento'];

// Sem o número (não veio da API), mostra 0: a contagem nunca some da legenda.
const plural = (n, um, varios) => {
    const valor = Number(n) || 0;
    return `${valor} ${valor === 1 ? um : varios}`;
};
const data = (v) => (v ? moment(v).format('DD/MM/YYYY') : '');
const periodo = (e) => {
    const inicio = data(e.data);
    const fim = data(e.dataFim);
    return fim && fim !== inicio ? `${inicio} a ${fim}` : inicio;
};

// As badges da emissão, na coluna Emissões (a sugestão vai ao RSVP no Enviar dela); as das malhas e das
// solicitações, na coluna Malhas.
const DA_EMISSAO = ['emEmissao', 'aEmitir', 'cancelados', 'emitidos', 'sugestao', 'sugestaoEnviada'];

// As pendências da linha, na ordem das colunas: cada uma abre a tela onde está o trabalho. As da emissão contam
// linhas da planilha, como foram enviadas ao RSVP. A que conta um só vai direto ao registro dela (`unicos` do
// back): o participante na emissão, as malhas da solicitação, a malha ou a solicitação.
const pendenciasDe = (e) => {
    const p = e.pendencias || {};
    const u = e.unicos || {};
    const emissao = `/cadastroEventoEmissaoAereo/${e.id}`;
    const solicitacoes = `/cadastroEventoSolicitacoesAereo/${e.id}`;
    const naEmissao = (chave) => (u[chave] ? `${emissao}?participante=${u[chave]}` : emissao);
    const malhasDa = (chave) => (u[chave] ? `/cadastroEventoMalhasAereo/${u[chave]}` : solicitacoes);
    const malha = (chave) => (u[chave] ? `/cadastroEventoImportarMalhaAereo/${u[chave]}` : solicitacoes);
    const solicitacao = (chave) => (u[chave] ? `/cadastroEventoSolicitacaoAereo/${u[chave]}` : solicitacoes);
    return [
        p.emEmissao > 0 && { chave: 'emEmissao', texto: `${p.emEmissao} em emissão`, tom: 'is-info', to: naEmissao('emEmissao'),
            titulo: 'Linhas fora de A EMITIR, de EMITIDO e de cancelado. O que está só no rascunho conta aqui até o Enviar.' },
        p.aEmitir > 0 && { chave: 'aEmitir', texto: `${p.aEmitir} a emitir`, tom: 'is-warn', to: naEmissao('aEmitir'),
            titulo: 'Linhas em A EMITIR: o pedido do RSVP esperando o Aéreo.' },
        p.cancelados > 0 && { chave: 'cancelados', texto: plural(p.cancelados, 'cancelado', 'cancelados'), tom: 'is-danger', to: naEmissao('cancelados'),
            titulo: 'Linhas canceladas.' },
        p.emitidos > 0 && { chave: 'emitidos', texto: plural(p.emitidos, 'emitido', 'emitidos'), tom: 'is-ok', to: naEmissao('emitidos'),
            titulo: 'Linhas em EMITIDO: o status, não o bilhete.' },
        // A sugestão, ao lado da emissão: vai ao RSVP no Enviar dela.
        p.sugestaoAEnviar && { chave: 'sugestao', texto: 'sugestão a enviar', tom: 'is-warn', to: `/cadastroEventoSugestaoAereo/${e.id}`,
            titulo: 'A sugestão de voos tem o que o RSVP ainda não recebeu: vai no Enviar para o RSVP da emissão.' },
        p.sugestaoEnviada && { chave: 'sugestaoEnviada', texto: 'sugestão enviada', tom: 'is-info', to: `/cadastroEventoSugestaoAereo/${e.id}`,
            titulo: 'A sugestão de voos já foi ao RSVP no Enviar para o RSVP da emissão.' },
        p.semMalha > 0 && { chave: 'semMalha', texto: `${p.semMalha} sem malha`, tom: 'is-warn', to: malhasDa('semMalha'),
            titulo: 'Solicitações enviadas ao Aéreo ainda sem nenhuma malha.' },
        p.rascunhos > 0 && { chave: 'rascunhos', texto: `${p.rascunhos} em rascunho`, tom: 'is-muted', to: malhasDa('rascunhos'),
            titulo: 'Solicitações em Solicitada com malha em rascunho: cada uma conta uma vez.' },
        p.malhasEmAlteracao > 0 && { chave: 'malhasEmAlteracao', texto: plural(p.malhasEmAlteracao, 'alteração necessária', 'alterações necessárias'), tom: 'is-warn', to: malha('malhasEmAlteracao'),
            titulo: 'Malhas em alteração: o RSVP pediu para mudar a malha aprovada, e o Aéreo a reenvia.' },
        p.enviadas > 0 && { chave: 'enviadas', texto: plural(p.enviadas, 'malha enviada', 'malhas enviadas'), tom: 'is-info', to: malha('enviadas'),
            titulo: 'Malhas que o Aéreo já enviou, em qualquer situação, menos as de solicitação cancelada.' },
        p.alteracoes > 0 && { chave: 'alteracoes', texto: plural(p.alteracoes, 'alteração solicitada', 'alterações solicitadas'), tom: 'is-info', to: solicitacao('alteracoes'),
            titulo: 'Solicitações em Alteração: o Aéreo pediu ajuste ao RSVP, ou o RSVP reabriu a solicitação.' },
        p.canceladas > 0 && { chave: 'canceladas', texto: plural(p.canceladas, 'solicitação cancelada', 'solicitações canceladas'), tom: 'is-danger', to: solicitacao('canceladas'),
            titulo: 'Solicitações canceladas que chegaram a ter malha, mesmo em rascunho.' },
    ].filter(Boolean);
};

// O ícone do card de emissões: o mesmo desenho do FaUsers, com cada pessoa (cabeça e corpo) na cor de um número, na
// ordem deles: a da esquerda em emissão, a do centro a emitir, a da direita cancelados.
const PESSOAS_DO_ICONE = [
    ['var(--fin-ac)', 'M96 224c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64z'
        + 'M173.1 274.6C161.5 263.1 145.6 256 128 256H64c-35.3 0-64 28.7-64 64v32c0 17.7 14.3 32 32 32h65.9c6.3-47.4 34.9-87.3 75.2-109.4z'],
    ['var(--fin-wn)', 'M320 256c61.9 0 112-50.1 112-112S381.9 32 320 32 208 82.1 208 144s50.1 112 112 112z'
        + 'M396.8 288h-8.3c-20.8 10-43.9 16-68.5 16s-47.6-6-68.5-16h-8.3C179.6 288 128 339.6 128 403.2V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-28.8c0-63.6-51.6-115.2-115.2-115.2z'],
    ['var(--fin-dg)', 'M544 224c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64z'
        + 'M576 256h-64c-17.6 0-33.5 7.1-45.1 18.6 40.3 22.1 68.9 62 75.1 109.4h66c17.7 0 32-14.3 32-32v-32c0-35.3-28.7-64-64-64z'],
];
function PessoasPorStatus() {
    return (
        <svg viewBox="0 0 640 512" width="15" height="15" aria-hidden="true">
            {PESSOAS_DO_ICONE.map(([cor, d]) => <path key={cor} d={d} style={{ fill: cor }} />)}
        </svg>
    );
}

// Os contadores de um card, cada um na cor da badge dele, separados por "·". `itens`: [[valor, cor], ...].
function Contadores({ itens }) {
    return itens.map(([valor, cor], i) => (
        <React.Fragment key={i}>
            {i > 0 && <span style={{ color: 'var(--fin-ft)', fontWeight: 400, margin: '0 8px' }}>·</span>}
            <span style={{ color: cor }}>{valor}</span>
        </React.Fragment>
    ));
}

/**
 * O filtro de pendências num modal: "Qualquer pendência" e as badges da coluna, em Emissões e Malhas. A escolha vale
 * no Aplicar; Cancelar ou fechar descarta.
 */
function FiltroPendencias({ valores, onAplicar, onFechar }) {
    const [marcados, setMarcados] = useState(valores);
    // "Qualquer pendência" e as específicas não valem juntas: marcar uma desmarca a outra.
    const alternar = (valor) => setMarcados(m => {
        if (m.includes(valor)) return m.filter(v => v !== valor);
        return valor === QUALQUER_PENDENCIA.value ? [valor] : [...m.filter(v => v !== QUALQUER_PENDENCIA.value), valor];
    });
    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg" onClick={ev => { if (ev.target === ev.currentTarget) onFechar(); }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaFilter size={17} /></span>
                        <h3 className="fin-pp__modal-title">Filtrar por pendência
                            <span className="fin-pp__modal-sub">O evento entra com qualquer uma das escolhidas.</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar}><FaTimes size={16} /></button>
                    </div>
                    <div className="modal-body">
                        <FinCheck id="pendencia-qualquer" label={QUALQUER_PENDENCIA.label}
                            checked={marcados.includes(QUALQUER_PENDENCIA.value)} onChange={() => alternar(QUALQUER_PENDENCIA.value)} />
                        <div className="fin-pp__filters" style={{ marginTop: 14, alignItems: 'flex-start' }}>
                            {GRUPOS_PENDENCIA.map(g => (
                                <FinField key={g.titulo} label={g.titulo}>
                                    <FinChecks>
                                        {g.itens.map(o => (
                                            <FinCheck key={o.value} id={'pendencia-' + o.value} label={o.label}
                                                checked={marcados.includes(o.value)} onChange={() => alternar(o.value)} />
                                        ))}
                                    </FinChecks>
                                </FinField>
                            ))}
                        </div>
                    </div>
                    <div className="fin-pp__modal-foot">
                        {marcados.length > 0 && (
                            <button type="button" className="fin-pp__btn" style={{ marginRight: 'auto' }} onClick={() => setMarcados([])}>Limpar</button>
                        )}
                        <button type="button" className="fin-pp__btn" onClick={onFechar}>Cancelar</button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={() => onAplicar(marcados)}>Aplicar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Quem atende o evento: na célula, os que agiram por último e "+n responsáveis"; o clique abre o mesmo popover
 * das observações da emissão com todos, cada um com as emissões e as malhas em que agiu.
 */
function Atendimento({ pessoas }) {
    if (!pessoas.length) {
        return <span className="fin-pp__cell-muted">Ninguém atende</span>;
    }
    const naCelula = pessoas.slice(0, NOMES_NA_CELULA);
    const deFora = pessoas.length - naCelula.length;
    return (
        <PopoverObservacao rotulo={`Atendimento (${pessoas.length})`}
            titulo="Clique para ver todos que atendem o evento, com o que cada um fez"
            conteudo={(
                // O popover vai para o body: o `.fin-pp` traz as cores das badges.
                <div className="fin-pp d-flex flex-column gap-2" style={{ whiteSpace: 'normal' }}>
                    {pessoas.map(p => (
                        <div key={p.id}>
                            <div className="d-flex align-items-center flex-wrap gap-1">
                                <strong style={{ marginRight: 4 }}>{p.nome}</strong>
                                {p.emissoes > 0 && <span className="fin-pp__minitag is-info">{plural(p.emissoes, 'emissão', 'emissões')}</span>}
                                {p.malhas > 0 && <span className="fin-pp__minitag is-info">{plural(p.malhas, 'malha', 'malhas')}</span>}
                            </div>
                            <div className="fin-pp__cell-muted" style={{ fontSize: 11 }}>
                                {p.ultimaAcaoEm
                                    ? `Última ação em ${moment(p.ultimaAcaoEm).format('DD/MM/YYYY HH:mm')}`
                                    : `Assumiu em ${data(p.assumiuEm)}, sem ação ainda`}
                            </div>
                        </div>
                    ))}
                </div>
            )}>
            {naCelula.map(p => (
                <span key={p.id} className="d-block text-truncate fin-pp__obs-linha fin-pp__obs-celula" style={{ fontSize: 12 }}>{p.nome}</span>
            ))}
            {deFora > 0 && (
                <span className="d-block fin-pp__cell-muted" style={{ fontSize: 11, marginTop: 2, fontWeight: 700 }}>
                    +{plural(deFora, 'responsável', 'responsáveis')}
                </span>
            )}
        </PopoverObservacao>
    );
}

/**
 * Tela Aéreo (`/aereo`): os eventos com aéreo, com a demanda, as pendências e quem atende cada um. Todo o Aéreo vê
 * todos os eventos e o que cada pessoa fez (`EventoAereoPainelService` no back). Os filtros ficam guardados
 * neste navegador, para voltar de um evento com o painel como estava.
 */
export default function PainelAereo() {
    const token = localStorage.getItem('token');
    const idUsuario = localStorage.getItem('idUsuario');
    const chaveFiltros = `aereo:filtros:${idUsuario || 'sem-usuario'}`;
    const [guardados] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(chaveFiltros) || 'null') || {};
        } catch (e) {
            return {};
        }
    });

    // As quatro situações guardadas de antes do "Todos" abrem como "Todos".
    const [situacoes, setSituacoes] = useState(() => {
        const lista = Array.isArray(guardados.situacoes) ? guardados.situacoes : SITUACOES_PADRAO;
        return SITUACOES.every(s => lista.includes(s.value)) ? [] : lista;
    });
    const [atendentes, setAtendentes] = useState(Array.isArray(guardados.atendentes) ? guardados.atendentes : []);
    const [fd, setFd] = useState(Array.isArray(guardados.fd) ? guardados.fd : []);
    // O "Só com pendência" guardado de antes vira "Qualquer pendência". A lista guardada com ela e específicas juntas,
    // de antes de uma desmarcar a outra, fica com as específicas.
    const [pendencias, setPendencias] = useState(() => {
        const lista = Array.isArray(guardados.pendencias) ? guardados.pendencias : (guardados.soPendentes ? [QUALQUER_PENDENCIA.value] : []);
        return lista.length > 1 ? lista.filter(v => v !== QUALQUER_PENDENCIA.value) : lista;
    });
    const [texto, setTexto] = useState('');
    const [buscar, setBuscar] = useState('');
    const [ordem, setOrdem] = useState('codigo:desc');
    const [pagina, setPagina] = useState({ page: 1, pageSize: PAGE_SIZE });
    const [dados, setDados] = useState({ eventos: [], pessoas: [], total: 0, page: 1, lastPage: 1, podeFechar: false });
    const [resumo, setResumo] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [recarga, setRecarga] = useState(0);
    const [acoesDe, setAcoesDe] = useState(null);
    const [filtroPendenciasAberto, setFiltroPendenciasAberto] = useState(false);
    const [eventoAtendimento, setEventoAtendimento] = useState(null);
    const [exportando, setExportando] = useState(false);
    // O evento do modal do Faturamento.
    const [fechamentoDe, setFechamentoDe] = useState(null);
    const buscaId = useRef(0);

    // Assumir ou tirar alguém do atendimento muda a coluna: o painel relê.
    const recarregar = useCallback(() => setRecarga(n => n + 1), []);
    const atendimento = useAtendimentoAereo(eventoAtendimento, recarregar);

    const setFullTrail = useSetFullBreadcrumbTrail();
    useEffect(() => {
        setFullTrail([{ path: '/aereo', label: 'Aéreo' }]);
    }, [setFullTrail]);

    // Os cards do topo saem do resumo sem filtro: lidos uma vez, não mudam com a busca da tabela.
    useEffect(() => {
        api({ method: 'get', action: '/eventoAereoPainel/resumo', token })
            .then(res => setResumo(res.data.data ?? null))
            .catch(err => notify.apiError(err, 'Erro ao carregar os números do Aéreo'));
    }, [token]);

    useEffect(() => {
        try {
            localStorage.setItem(chaveFiltros, JSON.stringify({ situacoes, atendentes, pendencias, fd }));
        } catch (e) {
            // Sem armazenamento, os filtros valem só nesta visita.
        }
    }, [chaveFiltros, situacoes, atendentes, pendencias, fd]);

    useEffect(() => {
        const id = ++buscaId.current;
        setCarregando(true);
        api({
            method: 'get', action: '/eventoAereoPainel', token,
            params: {
                situacao: situacoes, atendente: atendentes, pendencias, fd, buscar, ordem,
                page: pagina.page, pageSize: pagina.pageSize,
            },
        })
            .then(res => {
                if (id !== buscaId.current) return;
                const r = res.data || {};
                setDados({
                    eventos: r.data ?? [], pessoas: r.pessoas ?? [],
                    total: r.total ?? 0, page: r.page ?? 1, lastPage: r.lastPage ?? 1, podeFechar: !!r.podeFechar,
                });
            })
            .catch(err => {
                if (id === buscaId.current) notify.apiError(err, 'Erro ao carregar os eventos do Aéreo');
            })
            .finally(() => {
                if (id === buscaId.current) setCarregando(false);
            });
    }, [token, situacoes, atendentes, pendencias, fd, buscar, ordem, pagina, recarga]);

    // Filtro novo volta para a primeira página.
    const primeiraPagina = () => setPagina(p => ({ ...p, page: 1 }));
    // A busca por texto vale no Enter ou no Buscar; os selects e o "Só com pendência" valem na hora.
    const aplicarBusca = () => {
        setBuscar(texto.trim());
        primeiraPagina();
    };
    const filtrosMexidos = texto !== '' || buscar !== '' || pendencias.length > 0 || atendentes.length > 0 || fd.length > 0
        || [...situacoes].sort().join() !== [...SITUACOES_PADRAO].sort().join();
    const limparFiltros = () => {
        setTexto('');
        setBuscar('');
        setSituacoes(SITUACOES_PADRAO);
        setAtendentes([]);
        setPendencias([]);
        setFd([]);
        primeiraPagina();
    };

    const opcoesAtendente = [
        ...(idUsuario ? [{ value: String(idUsuario), label: 'Meus eventos' }] : []),
        { value: SEM_ATENDENTE, label: 'Sem atendente' },
        ...dados.pessoas.filter(p => String(p.id) !== String(idUsuario)).map(p => ({ value: String(p.id), label: p.nome })),
    ];

    const [colunaOrdem, direcaoOrdem] = ordem.split(':');
    const ordenar = (coluna) => {
        setOrdem(colunaOrdem === coluna && direcaoOrdem === 'desc' ? `${coluna}:asc` : `${coluna}:desc`);
        primeiraPagina();
    };
    const thOrdenavel = (label, coluna, style) => (
        <th className="is-sortable" style={style} onClick={() => ordenar(coluna)}>
            {label}{colunaOrdem === coluna ? (direcaoOrdem === 'asc' ? ' ▲' : ' ▼') : ''}
        </th>
    );

    const abrirAtendimento = (e) => {
        setAcoesDe(null);
        setEventoAtendimento(String(e.id));
        atendimento.abrir();
    };

    // O Exportar Aéreo do Evento: a planilha de emissão com as malhas enviadas e a aba Orientações na frente
    // (`malhas=1`), gerada na hora. O item fica preso até o arquivo chegar.
    const exportarAereo = async (e) => {
        setExportando(true);
        try {
            await baixarBlob(`${API}/eventoAereoEmissaoPlanilha?evento=${e.id}&malhas=1`, token, 'Aereo-do-Evento.xlsx');
        } finally {
            setExportando(false);
        }
    };

    // O menu da linha: as telas do aéreo do evento que existem, o Exportar Aéreo do Evento e o atendimento.
    // No drawer, por seção (`GRUPOS_DO_MENU`); os rótulos são os mesmos do menu do painel de eventos.
    const acoesDoEvento = (e) => [
        e.solicitacoes > 0 && { grupo: 'Malha e emissão', label: 'Importação Malha Aérea',
            icon: <FaUpload size={16} />, to: `/cadastroEventoSolicitacoesAereo/${e.id}` },
        e.naEmissao > 0 && { grupo: 'Malha e emissão', label: 'Emissão de Aéreo',
            icon: <FaUpload size={16} />, to: `/cadastroEventoEmissaoAereo/${e.id}` },
        // A sugestão vai ao RSVP no Enviar da emissão: só com ela.
        e.naEmissao > 0 && { grupo: 'Malha e emissão', label: 'Sugestão de Voos',
            icon: <FaPlane size={16} />, to: `/cadastroEventoSugestaoAereo/${e.id}` },
        (e.solicitacoes > 0 || e.naEmissao > 0) && { grupo: 'Malha e emissão', label: exportando ? 'Exportando...' : 'Exportar Aéreo do Evento',
            icon: <FaFileExcel size={16} />, action: () => exportarAereo(e), disabled: exportando },
        { grupo: 'Atendimento', label: 'Atendimento', icon: <FaHeadset size={16} />, action: () => abrirAtendimento(e) },
    ].filter(Boolean);

    const totalPaginas = Math.max(1, dados.lastPage || 1);
    const p = dados.page || 1;

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Aéreo</h1>
                        <p className="fin-pp__subtitle">Painel de aéreo · demanda, pendências e atendimento.</p>
                    </div>
                    <div className="fin-pp__actions">
                        <Link to="/rascunhoMalhaAereo" className="fin-pp__btn fin-pp__btn--pill"
                            title="Monta uma malha só neste navegador, sem ir ao sistema.">
                            <FaPencilAlt size={12} />
                            Rascunho de Malha
                        </Link>
                    </div>
                </div>

                {/* ── KPIs: todos os eventos com aéreo, sem filtro ──────────────── */}
                <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Eventos com aéreo</span>
                            <span className="fin-pp__kpi-ic"><FaPlane size={15} /></span>
                        </div>
                        <div className="fin-pp__kpi-value">{resumo ? resumo.eventos : '—'}</div>
                        {/* Toda linha da emissão e toda malha, a de sugestão e a em rascunho inclusive. */}
                        {resumo && (
                            <div className="fin-pp__kpi-sub">
                                {plural(resumo.emissoes, 'emissão', 'emissões')} · {plural(resumo.malhas, 'malha/sugestão', 'malhas/sugestões')}
                            </div>
                        )}
                    </div>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Bilhetes emitidos</span>
                            <span className="fin-pp__kpi-ic is-ok"><FaTicketAlt size={15} /></span>
                        </div>
                        <div className="fin-pp__kpi-value is-ok">{resumo ? resumo.bilhetesEmitidos : '—'}</div>
                        {/* Os participantes desses bilhetes (um participante tem até dois, ida e volta, e mais na reemissão)
                            e o Duplicado que o Aéreo enviou mesmo assim, que está entre eles. */}
                        {resumo && (
                            <div className="fin-pp__kpi-sub">
                                {plural(resumo.participantesComBilhete, 'participante', 'participantes')} · {plural(resumo.bilhetesDuplicados, 'duplicado', 'duplicados')}
                            </div>
                        )}
                    </div>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Malhas enviadas</span>
                            <span className="fin-pp__kpi-ic is-info"><FaPaperPlane size={14} /></span>
                        </div>
                        <div className="fin-pp__kpi-value is-info">{resumo ? resumo.malhasEnviadas : '—'}</div>
                        {/* A sugestão enviada fica fora das malhas enviadas e aparece aqui, mesmo com 0. */}
                        {resumo && (
                            <div className="fin-pp__kpi-sub">
                                {plural(resumo.solicitacoes, 'solicitação', 'solicitações')} · {plural(resumo.sugestoesEnviadas, 'sugestão', 'sugestões')}
                            </div>
                        )}
                    </div>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Em emissão · A emitir · Cancelados</span>
                            <span className="fin-pp__kpi-ic is-warn"><PessoasPorStatus /></span>
                        </div>
                        <div className="fin-pp__kpi-value">
                            {resumo ? <Contadores itens={[[resumo.emEmissao, 'var(--fin-ac)'], [resumo.aEmitir, 'var(--fin-wn)'], [resumo.cancelados, 'var(--fin-dg)']]} /> : '—'}
                        </div>
                        {/* Os três números contam linhas; embaixo, os participantes delas. */}
                        {resumo && <div className="fin-pp__kpi-sub">{plural(resumo.participantes, 'participante', 'participantes')}</div>}
                    </div>
                    <div className="fin-pp__kpi">
                        <div className="fin-pp__kpi-top">
                            <span className="fin-pp__kpi-label">Pendências/Eventos</span>
                            <span className="fin-pp__kpi-ic is-warn"><FaExclamationTriangle size={14} /></span>
                        </div>
                        <div className="fin-pp__kpi-value is-warn">
                            {resumo ? <>{resumo.pendencias}<span style={{ color: 'var(--fin-ft)' }}>/</span>{resumo.eventosComPendencia}</> : '—'}
                        </div>
                        {/* De onde vêm as pendências: a soma das duas dá o total. A sugestão a enviar não é pendência. */}
                        {resumo && (
                            <div className="fin-pp__kpi-sub">
                                {plural(resumo.pendenciasEmissoes, 'emissão', 'emissões')} · {plural(resumo.pendenciasSolicitacoes, 'solicitação', 'solicitações')}
                            </div>
                        )}
                    </div>
                </section>

                {/* ── Eventos ──────────────────────────────────────────────── */}
                <div className="fin-pp__region">
                    <div className="fin-pp__cphead">
                        <span className="fin-pp__cphead-ic"><FaPlane size={18} /></span>
                        <div style={{ minWidth: 0 }}>
                            <h2 className="fin-pp__cphead-title">Eventos</h2>
                            <p className="fin-pp__cphead-sub">Cada pendência abre a tela específica, e o atendimento mostra o que cada pessoa fez.</p>
                        </div>
                    </div>
                    <div className="fin-pp__region-body">
                        <div className="fin-pp__card mb-3" style={{ padding: 16 }}>
                            <div className="fin-pp__filters">
                                <FinField label="Evento ou cliente" grow>
                                    <FinInput value={texto} onChange={ev => setTexto(ev.target.value)}
                                        onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); aplicarBusca(); } }}
                                        placeholder="Nome, código, identificação ou cliente" />
                                </FinField>
                                <FinField label="Situação" style={{ flex: '1 1 280px' }}>
                                    <ReactSelect isMulti options={[TODAS_SITUACOES, ...SITUACOES]} placeholder="Todos"
                                        // Com o "Todos", o limpar também não mudaria nada.
                                        isClearable={situacoes.length > 0} styles={ESTILO_SITUACOES}
                                        value={situacoes.length ? SITUACOES.filter(s => situacoes.includes(s.value)) : [TODAS_SITUACOES]}
                                        onChange={sel => { setSituacoes(situacoesEscolhidas(sel)); primeiraPagina(); }} />
                                </FinField>
                                <FinField label="Atendente" style={{ flex: '1 1 240px' }}>
                                    <ReactSelect isMulti options={opcoesAtendente} placeholder="Todos"
                                        value={opcoesAtendente.filter(o => atendentes.includes(o.value))}
                                        onChange={sel => { setAtendentes((sel || []).map(o => o.value)); primeiraPagina(); }} />
                                </FinField>
                                <FinField label="Pendências" style={{ flex: '1 1 260px' }}>
                                    <button type="button" className="fin-pp__btn" onClick={() => setFiltroPendenciasAberto(true)}
                                        title={pendencias.map(v => ROTULO_PENDENCIA[v]).filter(Boolean).join(', ') || 'Sem filtro'}
                                        style={{ height: 42, width: '100%', justifyContent: 'space-between' }}>
                                        <span className="text-truncate">
                                            {pendencias.map(v => ROTULO_PENDENCIA[v]).filter(Boolean).join(', ') || 'Sem filtro'}
                                        </span>
                                        <FaFilter size={12} />
                                    </button>
                                </FinField>
                                {dados.podeFechar && (
                                    <FinField label="Fechado/Despachado" style={{ flex: '1 1 200px' }}>
                                        <ReactSelect isMulti options={OPCOES_FD} placeholder="Todos"
                                            value={OPCOES_FD.filter(o => fd.includes(o.value))}
                                            onChange={sel => { setFd((sel || []).map(o => o.value)); primeiraPagina(); }} />
                                    </FinField>
                                )}
                                <div className="fin-pp__filters-actions">
                                    {filtrosMexidos && (
                                        <button type="button" className="fin-pp__btn" title="Limpar filtros" onClick={limparFiltros}>
                                            <FaTimes size={13} /> Limpar
                                        </button>
                                    )}
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={aplicarBusca}>
                                        <FaSearch size={13} /> Buscar
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl" style={{ minWidth: 1200, tableLayout: 'auto' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 56 }}>Ações</th>
                                            {thOrdenavel('Evento', 'codigo')}
                                            {thOrdenavel('Data', 'data', { width: 120 })}
                                            <th style={{ width: 110 }}>Situação</th>
                                            <th style={{ width: 150 }}>Demanda</th>
                                            <th>Emissões</th>
                                            <th>Malhas</th>
                                            {dados.podeFechar && <th style={{ width: 150 }} title="F · D: fechado e despachado">Faturamento</th>}
                                            <th style={{ width: 200 }}>Atendimento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carregando ? (
                                            <tr><td colSpan={dados.podeFechar ? 9 : 8} className="fin-pp__empty" style={{ padding: 0 }}><TabelaLoading /></td></tr>
                                        ) : dados.eventos.length === 0 ? (
                                            <tr><td colSpan={dados.podeFechar ? 9 : 8} className="fin-pp__empty" style={{ padding: 0 }}><TabelaVazia texto="Nenhum evento com aéreo neste filtro" /></td></tr>
                                        ) : dados.eventos.map(e => {
                                            const situacao = SITUACOES.find(s => s.value === String(e.situacao ?? '').toLowerCase());
                                            const pendencias = pendenciasDe(e);
                                            return (
                                                <tr key={e.id}>
                                                    <td>
                                                        <button type="button" className="fin-pp__rowbtn" title="Ações do evento"
                                                            onClick={() => setAcoesDe(e)}>
                                                            <FaEllipsisV size={14} />
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div className="fin-pp__cell-strong">{e.nome}</div>
                                                        <div className="fin-pp__cell-muted" style={{ fontSize: 12 }}>
                                                            {[e.codigo, e.identificacao, e.cliente].filter(Boolean).join(' · ')}
                                                        </div>
                                                    </td>
                                                    <td className="fin-pp__cell-muted">{periodo(e) || '—'}</td>
                                                    <td>
                                                        {situacao
                                                            ? <span className={'fin-pp__pill ' + situacao.tom}>{situacao.label}</span>
                                                            : <span className="fin-pp__cell-muted">{e.situacao || '—'}</span>}
                                                    </td>
                                                    <td className="fin-pp__cell-muted" style={{ fontSize: 12 }}>
                                                        <div>{plural(e.naEmissao, 'participante', 'participantes')}</div>
                                                        <div>{plural(e.solicitacoes, 'solicitação', 'solicitações')}</div>
                                                    </td>
                                                    {[pendencias.filter(x => DA_EMISSAO.includes(x.chave)),
                                                        pendencias.filter(x => !DA_EMISSAO.includes(x.chave))].map((lista, i) => (
                                                        <td key={i}>
                                                            {lista.length === 0 ? (
                                                                <span className="fin-pp__cell-muted">Nenhuma</span>
                                                            ) : (
                                                                <div className="d-flex flex-wrap gap-1">
                                                                    {lista.map(x => (
                                                                        <Link key={x.chave} to={x.to} title={x.titulo}
                                                                            className={'fin-pp__pill ' + x.tom} style={{ textDecoration: 'none' }}>
                                                                            {x.texto}
                                                                        </Link>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    ))}
                                                    {dados.podeFechar && (
                                                        <td>
                                                            {/* O clipe abre o modal do fechamento; F (com anexo) e D (despachado) só
                                                                aparecem marcados, e o anexo baixa no link embaixo. */}
                                                            <div className="d-flex align-items-center gap-2">
                                                                <button type="button" className="fin-pp__rowbtn" title="Fechamento e despacho"
                                                                    aria-label="Fechamento e despacho" onClick={() => setFechamentoDe(e)}>
                                                                    <FaPaperclip size={14} />
                                                                </button>
                                                                <span style={{ fontWeight: 700, fontSize: 13 }}>
                                                                    {e.fechamentoAereo?.anexo && (
                                                                        <span style={{ color: 'var(--fin-ok)' }}
                                                                            title={`Fechado em ${quandoQuem(e.fechamentoAereo.fechadoEm, e.fechamentoAereo.fechadoPor)}`}>F</span>
                                                                    )}
                                                                    {e.fechamentoAereo?.anexo && e.fechamentoAereo?.despachadoEm && (
                                                                        <span style={{ color: 'var(--fin-ft)', margin: '0 4px' }}>·</span>
                                                                    )}
                                                                    {e.fechamentoAereo?.despachadoEm && (
                                                                        <span style={{ color: 'var(--fin-ac)' }}
                                                                            title={`Despachado em ${quandoQuem(e.fechamentoAereo.despachadoEm, e.fechamentoAereo.despachadoPor)}`}>D</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            {e.fechamentoAereo?.anexo && (
                                                                <button type="button" className="text-truncate d-block" title={e.fechamentoAereo.anexo}
                                                                    onClick={() => baixarBlob(`${API}/eventoAereoFechamentoArquivo?evento=${e.id}`, token, e.fechamentoAereo.anexo)}
                                                                    style={{ border: 0, background: 'none', padding: 0, marginTop: 4, maxWidth: 150, fontSize: 12, color: 'var(--fin-ac)', textDecoration: 'underline' }}>
                                                                    {e.fechamentoAereo.anexo}
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td><Atendimento pessoas={e.atendimento || []} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {dados.total > 0 && (
                                <div className="fin-pp__pagination">
                                    <div className="fin-pp__pagination-info">
                                        {((p - 1) * pagina.pageSize) + 1}–{Math.min(p * pagina.pageSize, dados.total)} de {dados.total}
                                    </div>
                                    <div className="fin-pp__pagination-ctrls">
                                        <select className="fin-pp__select" style={{ height: 34 }} value={pagina.pageSize}
                                            onChange={ev => setPagina({ page: 1, pageSize: Number(ev.target.value) })}>
                                            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pág.</option>)}
                                        </select>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={p <= 1}
                                            onClick={() => setPagina(x => ({ ...x, page: p - 1 }))}>Anterior</button>
                                        <span className="fin-pp__pagination-info">{p} / {totalPaginas}</span>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={p >= totalPaginas}
                                            onClick={() => setPagina(x => ({ ...x, page: p + 1 }))}>Próxima</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {acoesDe && (
                    <div className="fin-pp fin-pp__drawer-backdrop" onClick={() => setAcoesDe(null)}>
                        <div className="fin-pp__drawer" onClick={ev => ev.stopPropagation()}>
                            <div className="fin-pp__drawer-head">
                                <span className="fin-pp__drawer-head-ic"><FaEllipsisV size={16} /></span>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <h3 className="fin-pp__drawer-title">Ações do evento</h3>
                                    <p className="fin-pp__drawer-sub">{acoesDe.nome}</p>
                                </div>
                                <button type="button" className="fin-pp__drawer-close" onClick={() => setAcoesDe(null)}><FaTimes size={16} /></button>
                            </div>
                            <div className="fin-pp__drawer-body">
                                {GRUPOS_DO_MENU.map(grupo => {
                                    const itens = acoesDoEvento(acoesDe).filter(a => a.grupo === grupo);
                                    if (!itens.length) return null;
                                    return (
                                        <div key={grupo}>
                                            <div className="fin-pp__drawer-section">{grupo}</div>
                                            {itens.map(a => {
                                                const corpo = (
                                                    <>
                                                        <span className="fin-pp__drawer-item-ic">{a.icon}</span>
                                                        <span className="fin-pp__drawer-item-label">{a.label}</span>
                                                    </>
                                                );
                                                return a.to
                                                    ? <Link key={a.label} to={a.to} className="fin-pp__drawer-item" onClick={() => setAcoesDe(null)}>{corpo}</Link>
                                                    : <button key={a.label} type="button" className="fin-pp__drawer-item" onClick={a.action} disabled={a.disabled}>{corpo}</button>;
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {fechamentoDe && (
                    <FechamentoAereoModal evento={fechamentoDe} onFechar={() => setFechamentoDe(null)}
                        onAplicado={() => { setFechamentoDe(null); recarregar(); }} />
                )}

                {filtroPendenciasAberto && (
                    <FiltroPendencias valores={pendencias} onFechar={() => setFiltroPendenciasAberto(false)}
                        onAplicar={valores => { setPendencias(valores); primeiraPagina(); setFiltroPendenciasAberto(false); }} />
                )}

                {atendimento.modal}
            </div>
        </ErpTemplate>
    );
}
